import React, {useMemo} from 'react';

import {AnalysisGraphQL, AnalysisViewGraphQL, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {isAnalysisOnlyChartStep} from 'analysis-tool/charting/AnalysisChartingUtils';
import getStepVisualizationConfigAndName from 'analysis-tool/data/getVisualizationConfigAndName';
import {getTitleForView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {AnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import VisualizationConfigEditor from 'insights/analytics-visualizations/configs/VisualizationConfigEditor';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import {assertPipelineStepIsStepType, PipelineStep} from 'pipelines/PipelineTypes';
import {
  getExpectedStepDataKind,
  getPipelineStepRawDatasOfKind,
  getSingleInputRawData,
} from 'pipelines/StepDataUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItem} from '../RightSidebarTypes';
import {saveChartOrTable} from '../utils/saveChartOrTable';
import EditViewChart from './edit-view/EditViewChart';
import EditViewTransforms from './edit-view/EditViewTransforms';
import RightSidebarSkeleton, {
  RightSidebarWidth,
  RightSidebarConfirmationButton,
} from './RightSidebarSkeleton';
import useMaybeDraftStep, {draftStepGlobalState} from './useMaybeDraftStep';
interface EditStepRightSidebarProps {
  analysis: AnalysisGraphQL;
  selectedRightSidebarItem: AnalysisRightSidebarItem;
  clientStateId: string;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem | null) => void;
}

/** @package */
export const visualizationConfigState = makeUseGlobalClientState<VisualizationConfig | null>(null);

export default function EditViewRightSidebar({
  analysis,
  selectedRightSidebarItem,
  clientStateId,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
}: EditStepRightSidebarProps): JSX.Element {
  const originalStep = analysis.steps.find(
    (originalStep) => originalStep.id === selectedRightSidebarItem.stepId
  );
  assert(
    originalStep != null,
    'Expected selectedRightSidebarItem.stepId to correspond to a step in the analysis'
  );

  const {draftStep, setDraftStep} = useMaybeDraftStep(originalStep.id, null);
  const [nullableVisualizationConfig, setVisualizationConfig] =
    visualizationConfigState.useGlobalClientState(clientStateId);
  const onChangeVisualizationConfig = (newVisualizationConfig: VisualizationConfig): void => {
    setVisualizationConfig(newVisualizationConfig);
    const visualizationType = getVisualizationType(newVisualizationConfig);
    const stepType =
      visualizationType === VisualizationType.TABLE
        ? PipelineStepType.TABLE_VIEW
        : PipelineStepType.CHART_VIEW;
    const name =
      originalStep.stepType === PipelineStepType.CHART_VIEW
        ? originalStep.config?.chartName
        : originalStep.config?.viewName;
    setDraftStep({
      ...originalStep,
      stepType,
      config:
        stepType === PipelineStepType.CHART_VIEW
          ? {
              chartName: name,
              chartConfig: getPersistedChartViewConfig(newVisualizationConfig),
            }
          : {
              viewName: name,
            },
    });
  };

  const dataFrames = isAnalysisInputStepType(originalStep.stepType)
    ? getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: originalStep.outputStepDatas.flatMap(({rawDatas}) => rawDatas),
      })
    : getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: originalStep.inputStepDatas.flatMap(({rawDatas}) => rawDatas),
      });

  assertAndContinue(
    dataFrames.length === 1,
    'Expected last node of view being edited to have exactly one input',
    {
      numDataFrames: dataFrames.length,
      stepId: originalStep.id,
    }
  );
  const dataFrameSpec = dataFrames[0];

  const contextValue = useMemo(() => ({dataFrameSpec}), [dataFrameSpec]);

  const confirmationButtons: Array<RightSidebarConfirmationButton> = [
    {
      text: 'Close',
      onClick: (): void => {
        onChangeSelectedRightSidebarItem(null);
        draftStepGlobalState.deleteState(originalStep.id);
        visualizationConfigState.deleteState(clientStateId);
      },
      isPrimary: originalStep.stepType !== PipelineStepType.CHART_VIEW,
    },
  ];
  if (
    originalStep.stepType === PipelineStepType.CHART_VIEW ||
    originalStep.stepType === PipelineStepType.TABLE_VIEW
  ) {
    confirmationButtons.push({
      text: 'Save settings',
      isPrimary: true,
      onClick: async (): Promise<void> => {
        if (isAnalysisOnlyChartStep(originalStep)) {
          // TODO(BNCH-119690): Move into saveChartOrTable when we allow switching between analysis only chart
          // types and other chart types
          await withLoadingScreen(
            PipelineDAO.updateAndRunPipelineStep(analysis.id, {
              stepId: originalStep.id,
              stepConfig: draftStep?.config ?? {},
            })
          );
        } else if (nullableVisualizationConfig != null) {
          await withLoadingScreen(
            saveChartOrTable({
              analysis,
              originalChartOrTableStep: originalStep as PipelineStep<
                PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW
              >,
              newChartConfig: {
                chartConfig: getPersistedChartViewConfig(nullableVisualizationConfig),
                chartName: originalStep.config.chartName,
              },
              newVisualizationConfig: nullableVisualizationConfig,
              inputDataFrameId: dataFrameSpec.id,
              onChangeSelectedRightSidebarItem,
              onChangeSelectedItem,
            }),
            'Saving changes...'
          );
        }
        onChangeSelectedRightSidebarItem(null);
        visualizationConfigState.deleteState(clientStateId);
        draftStepGlobalState.deleteState(originalStep.id);
      },
    });
  }

  const view = analysis.views.find((view) =>
    view.lineage.some((node) => node.step.id === selectedRightSidebarItem.stepId)
  );
  if (view == null) {
    // If a new step gets added in filter and transforms, we need to wait until the analysis is refreshed
    // for the new step to be in the view.
    // TODO(BNCH-124718): Update the stepId in the RightSidebarItem to be a view instead
    return <CenteredHorizontalThrobber />;
  }

  return (
    <RightSidebarSkeleton
      title={getTitleForView(view)}
      width={RightSidebarWidth.STANDARD}
      confirmationButtons={confirmationButtons}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <EditViewRawData
          analysis={analysis}
          clientStateId={clientStateId}
          originalStep={originalStep}
          view={view}
          onChangeVisualizationConfig={onChangeVisualizationConfig}
          nullableVisualizationConfig={nullableVisualizationConfig}
        />
        <EditViewTransforms analysis={analysis} view={view} dataFrameSpec={dataFrameSpec} />
      </StepConfigEditorContext.Provider>
    </RightSidebarSkeleton>
  );
}

interface EditViewRawDataProps {
  analysis: AnalysisGraphQL;
  clientStateId: string;
  originalStep: PipelineStep;
  view: AnalysisViewGraphQL;
  onChangeVisualizationConfig: (newVisualizationConfig: VisualizationConfig) => void;
  nullableVisualizationConfig: VisualizationConfig | null;
}

/** @package */
export function EditViewRawData({
  analysis,
  clientStateId,
  originalStep,
  view,
  onChangeVisualizationConfig,
  nullableVisualizationConfig,
}: EditViewRawDataProps): JSX.Element | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  const viewStepDataKind = getExpectedStepDataKind(
    originalStep,
    lastNode.stepOutputReference!.outputStepData
  );

  if (
    originalStep.stepType === PipelineStepType.TABLE_VIEW ||
    (viewStepDataKind === PipelineStepDataKind.CHART && !isAnalysisOnlyChartStep(originalStep))
  ) {
    assert(
      [PipelineStepType.CHART_VIEW, PipelineStepType.TABLE_VIEW].includes(originalStep.stepType),
      'Expected originalStep type to be TABLE_VIEW or CHART_VIEW'
    );
    const dataFrame = getSingleInputRawData<
      PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
      0,
      PipelineStepDataKind.DATASET
    >(originalStep as PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW, 0>);
    const {visualizationConfig} = getStepVisualizationConfigAndName(
      getLegacyStepFromAnalysis(analysis, originalStep)
    );
    return (
      <VisualizationConfigEditor
        columns={dataFrame.columns}
        typeSelectorIsDisabled={false}
        disabledVisualizationTypes={[]}
        isAnalysisTool={true}
        onChangeVisualizationConfig={onChangeVisualizationConfig}
        visualizationConfig={nullableVisualizationConfig ?? visualizationConfig}
        useSystemNames={true}
        enableAnalysisTab={false}
        enableAggregation={false}
        enableFormatTab={undefined}
        enableFormatTabForLineChart={true}
        enableErrorBarColumn={true}
        isDisabled={false}
      />
    );
  } else if (viewStepDataKind === PipelineStepDataKind.CHART) {
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalStep);
    return (
      <EditViewChart analysis={analysis} clientStateId={clientStateId} step={originalStep} view={view} />
    );
  } else if (viewStepDataKind === PipelineStepDataKind.DATASET) {
    return null;
  } else if (viewStepDataKind === PipelineStepDataKind.FILE) {
    assertAndContinue(false, 'File views are not implemented yet');
    return null;
  } else {
    throw new Error(`Unexpected step data kind: ${viewStepDataKind}`);
  }
}
